/**
 * ANALYTICAY Print Shop Kanban - Main Application
 * 
 * This application has been refactored into a modular structure:
 * - /context: AuthContext for authentication state
 * - /components/kanban: KanbanBoard, ArtworkCard
 * - /components/shipping: ShippingTable
 * - /components/admin: UserManagement, AuditLogs
 * - /pages: LandingPage
 */
import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Plus, Trash2, Edit2, Check, X, Package, Truck, Users, Shield, LogOut, Clock, FileText, DollarSign, Mail, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

// Import modular components
import { AuthProvider, useAuth, API } from "@/context";
import { KanbanBoard } from "@/components/kanban";
import { ShipmentsTable } from "@/components/shipping";
import { UserManagement, AuditLogs, ProofApprovalManagement } from "@/components/admin";
import { LandingPage } from "@/pages";

// Invoice Management Component
function InvoiceManagement() {
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({ quantity: 0, print_quantity: 0, page_count: 1, rate: 0 });
  const [editingArtworkType, setEditingArtworkType] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [isMigrating, setIsMigrating] = useState(false);
  const [editingShippingTax, setEditingShippingTax] = useState(null);
  const [shippingTaxValues, setShippingTaxValues] = useState({ shipping_cost: 0, tax_cost: 0 });

  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/invoices`);
      setInvoiceData(response.data);
    } catch (error) {
      toast.error("Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPricingTiers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/pricing-tiers`);
      setPricingTiers(r.data);
    } catch (e) {
      // Silent — rate auto-lookup just won't work
    }
  }, []);

  useEffect(() => { fetchInvoiceData(); fetchPricingTiers(); }, [fetchInvoiceData, fetchPricingTiers]);

  // Look up rate from rate card for a given artwork type + quantity
  const lookupRateFromCard = (artworkType, qty) => {
    if (!artworkType || qty <= 0) return null;
    const sorted = pricingTiers
      .filter(t => t.artwork_type === artworkType)
      .sort((a, b) => a.min_quantity - b.min_quantity);
    for (const t of sorted) {
      if (t.min_quantity <= qty && (t.max_quantity === null || t.max_quantity === undefined || qty <= t.max_quantity)) {
        return t.rate;
      }
    }
    return null;
  };

  // Update editValues, and if the changed field affects rate lookup, auto-update rate too
  const updateEditValuesWithRate = (changes) => {
    const next = { ...editValues, ...changes };
    if ((changes.print_quantity !== undefined || changes.quantity !== undefined) && editingArtworkType) {
      const lookupQty = next.print_quantity > 0 ? next.print_quantity : next.quantity;
      const rate = lookupRateFromCard(editingArtworkType, lookupQty);
      if (rate !== null) next.rate = rate;
    }
    setEditValues(next);
  };

  const runSequenceMigration = async () => {
    if (!window.confirm("Run sequence migration? This will update all artworks to have correct sequence numbers (1-6). Safe to run multiple times.")) return;
    
    setIsMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-sequences`);
      toast.success(`Migration complete! Updated ${response.data.updated} artworks, skipped ${response.data.skipped} already correct.`);
      await fetchInvoiceData(); // Refresh to show new sequence
    } catch (error) {
      toast.error("Migration failed: " + (error.response?.data?.detail || "Unknown error"));
    } finally {
      setIsMigrating(false);
    }
  };

  const toggleInvoiceSent = async (eventId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/invoice-sent?sent=${!currentStatus}`);
      toast.success(`Invoice marked as ${!currentStatus ? 'sent' : 'not sent'}`);
      fetchInvoiceData();
    } catch (error) {
      toast.error("Failed to update invoice status");
    }
  };

  const startEditing = (row) => {
    if (row.is_frozen) { toast.error("Cannot edit frozen invoice."); return; }
    setEditingRow(row.artwork_id);
    setEditingArtworkType(row.artwork_type);
    setEditValues({ quantity: row.quantity, print_quantity: row.print_quantity, page_count: row.page_count || 1, rate: row.rate });
  };

  const saveEditing = async (artworkId) => {
    try {
      await axios.put(`${API}/admin/artworks/${artworkId}/pricing?quantity=${editValues.quantity}&print_quantity=${editValues.print_quantity}&page_count=${editValues.page_count}&rate=${editValues.rate}`);
      toast.success("Pricing updated");
      setEditingRow(null);
      fetchInvoiceData();
    } catch (error) {
      toast.error("Failed to update pricing");
    }
  };

  const startEditingShippingTax = (eventId, shipping, tax, isFrozen) => {
    if (isFrozen) { toast.error("Cannot edit frozen invoice."); return; }
    setEditingShippingTax(eventId);
    setShippingTaxValues({ shipping_cost: shipping, tax_cost: tax });
  };

  const saveShippingTax = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/shipping-tax?shipping_cost=${shippingTaxValues.shipping_cost}&tax_cost=${shippingTaxValues.tax_cost}`);
      toast.success("Shipping and tax updated");
      setEditingShippingTax(null);
      fetchInvoiceData();
    } catch (error) {
      toast.error("Failed to update: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const groupedData = invoiceData.reduce((acc, row) => {
    if (!acc[row.event_id]) {
      acc[row.event_id] = { 
        event_name: row.event_name, 
        event_id: row.event_id, 
        invoice_sent: row.invoice_sent, 
        artworks: [], 
        subtotal: 0,
        shipping_cost: row.shipping_cost || 0,
        tax_cost: row.tax_cost || 0,
        is_frozen: row.is_frozen
      };
    }
    acc[row.event_id].artworks.push(row);
    acc[row.event_id].subtotal += row.invoice_amount;
    return acc;
  }, {});

  // Sort artworks by sequence within each event
  Object.values(groupedData).forEach(eventGroup => {
    eventGroup.artworks.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    eventGroup.grand_total = eventGroup.subtotal + eventGroup.shipping_cost + eventGroup.tax_cost;
  });

  // Detect rows that have qty > 0 but rate = 0 (data hygiene warning)
  const zeroRateRows = invoiceData.filter(
    r => r.rate === 0 && ((r.print_quantity || 0) > 0 || (r.quantity || 0) > 0)
  );

  const runBackfill = async () => {
    if (!window.confirm(`Re-run rate backfill from the rate card? This will update rates for any artworks that currently have rate=$0 but a quantity set, using your Pricing Tiers.`)) return;
    try {
      const r = await axios.post(`${API}/admin/backfill-artwork-rates?dry_run=false`);
      const updated = r.data.updated_count || 0;
      const skippedTier = r.data.skipped_no_matching_tier || 0;
      toast.success(`Backfill complete: ${updated} artwork(s) updated${skippedTier > 0 ? `, ${skippedTier} still missing a matching tier (rate card gap)` : ""}`);
      await fetchInvoiceData();
    } catch (e) {
      toast.error("Backfill failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold tracking-tight">Invoice Management</h2>
        {invoiceData.length > 0 && (
          <Button 
            onClick={runSequenceMigration} 
            disabled={isMigrating}
            variant="outline"
            size="sm"
            className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            {isMigrating ? "Running Migration..." : "🔄 Fix Sequence Order"}
          </Button>
        )}
      </div>

      {zeroRateRows.length > 0 && (
        <div
          data-testid="zero-rate-banner"
          className="mb-6 border-2 border-red-400 bg-red-50 px-4 py-3 flex items-start justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-900 text-sm">
                {zeroRateRows.length} invoice row{zeroRateRows.length === 1 ? "" : "s"} have qty &gt; 0 but rate = $0
              </p>
              <p className="text-xs text-red-800 mt-1">
                Likely cause: rate card gap or missing tier. Click "Fix Now" to re-apply rates from the rate card. Rows still missing a tier afterwards point to a gap you'll need to close in the Pricing Tiers tab.
              </p>
            </div>
          </div>
          <Button
            data-testid="zero-rate-fix-btn"
            onClick={runBackfill}
            className="bg-red-600 text-white hover:bg-red-700 rounded-none h-9 px-4 text-sm shrink-0"
          >
            Fix Now
          </Button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center h-[400px]"><p className="text-muted-foreground">Loading...</p></div>
      ) : Object.values(groupedData).length === 0 ? (
        <div className="border border-border bg-white p-8 text-center"><p className="text-muted-foreground">No events with invoice data yet</p></div>
      ) : (
        <div className="space-y-8">
          {Object.values(groupedData).map((eventGroup) => (
            <div key={eventGroup.event_id} className="border border-border bg-white">
              <div className="bg-[#F4F4F5] border-b border-border p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight">{eventGroup.event_name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Subtotal: <span className="font-semibold text-foreground">${eventGroup.subtotal.toFixed(2)}</span>
                    {" | "}Shipping: <span className="font-semibold text-foreground">${eventGroup.shipping_cost.toFixed(2)}</span>
                    {" | "}Tax: <span className="font-semibold text-foreground">${eventGroup.tax_cost.toFixed(2)}</span>
                    {" | "}Grand Total: <span className="font-bold text-lg text-foreground">${eventGroup.grand_total.toFixed(2)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {eventGroup.invoice_sent && <span className="text-xs font-medium px-3 py-1 bg-blue-100 text-blue-800 rounded-sm border border-blue-300">FROZEN</span>}
                  <span className={`text-xs font-medium px-3 py-1 rounded-sm ${eventGroup.invoice_sent ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {eventGroup.invoice_sent ? "Invoice Sent" : "Not Sent"}
                  </span>
                  <Button onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/admin/invoices/export/${eventGroup.event_id}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a'); link.href = url; link.download = `invoice_${eventGroup.event_name}.csv`; link.click();
                    } catch (error) { toast.error("Failed to export"); }
                  }} size="sm" className="rounded-none bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)]" data-testid={`export-invoice-${eventGroup.event_id}`}>
                    <FileText className="h-3 w-3 mr-1" /> Export CSV
                  </Button>
                  <Button onClick={() => toggleInvoiceSent(eventGroup.event_id, eventGroup.invoice_sent)} size="sm" disabled={eventGroup.invoice_sent}
                    className={`rounded-none ${eventGroup.invoice_sent ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"} text-white`}>
                    {eventGroup.invoice_sent ? "Already Sent" : "Mark as Sent"}
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold w-16">#</TableHead>
                    <TableHead className="font-semibold">Artwork Type</TableHead>
                    <TableHead className="font-semibold">Quantity</TableHead>
                    <TableHead className="font-semibold">Print Qty</TableHead>
                    <TableHead className="font-semibold">Page Count</TableHead>
                    <TableHead className="font-semibold">Rate ($)</TableHead>
                    <TableHead className="font-semibold">Invoice Amount ($)</TableHead>
                    <TableHead className="font-semibold">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventGroup.artworks.map((row) => (
                    <TableRow key={row.artwork_id}>
                      <TableCell className="font-semibold text-muted-foreground">{row.sequence || '-'}</TableCell>
                      <TableCell className="font-medium">{row.artwork_type}</TableCell>
                      <TableCell>{editingRow === row.artwork_id ? <Input type="number" value={editValues.quantity} onChange={(e) => updateEditValuesWithRate({ quantity: parseInt(e.target.value) || 0 })} className="w-20 h-8 rounded-none border-2" /> : row.quantity}</TableCell>
                      <TableCell>{editingRow === row.artwork_id ? <Input type="number" value={editValues.print_quantity} onChange={(e) => updateEditValuesWithRate({ print_quantity: parseInt(e.target.value) || 0 })} className="w-20 h-8 rounded-none border-2" /> : row.print_quantity}</TableCell>
                      <TableCell>{editingRow === row.artwork_id ? <Input type="number" value={editValues.page_count} onChange={(e) => setEditValues({ ...editValues, page_count: parseInt(e.target.value) || 1 })} className="w-16 h-8 rounded-none border-2" min="1" /> : (row.page_count || 1)}</TableCell>
                      <TableCell>{editingRow === row.artwork_id ? <Input type="number" step="0.01" value={editValues.rate} onChange={(e) => setEditValues({ ...editValues, rate: parseFloat(e.target.value) || 0 })} className="w-20 h-8 rounded-none border-2" /> : (() => {
                        const matchesRateCard = row.rate_card_rate > 0 && Math.abs(row.rate - row.rate_card_rate) < 0.01;
                        const isDifferent = row.rate_card_rate > 0 && Math.abs(row.rate - row.rate_card_rate) >= 0.01;
                        const bgColor = matchesRateCard ? "bg-green-100 text-green-800 border border-green-400" : isDifferent ? "bg-red-100 text-red-800 border border-red-400" : "";
                        return <span className={`font-medium px-2 py-1 rounded-sm ${bgColor}`}>${row.rate.toFixed(2)}</span>;
                      })()}</TableCell>
                      <TableCell className="font-semibold">{editingRow === row.artwork_id ? `$${((editValues.print_quantity > 0 ? editValues.print_quantity : editValues.quantity) * editValues.page_count * editValues.rate).toFixed(2)}` : (() => {
                        const matchesRateCard = row.rate_card_rate > 0 && Math.abs(row.rate - row.rate_card_rate) < 0.01;
                        const isDifferent = row.rate_card_rate > 0 && Math.abs(row.rate - row.rate_card_rate) >= 0.01;
                        const bgColor = matchesRateCard ? "bg-green-100 text-green-800 border border-green-400" : isDifferent ? "bg-red-100 text-red-800 border border-red-400" : "";
                        return <span className={`font-semibold px-2 py-1 rounded-sm ${bgColor}`}>${row.invoice_amount.toFixed(2)}</span>;
                      })()}</TableCell>
                      <TableCell>
                        {editingRow === row.artwork_id ? (
                          <div className="flex gap-2">
                            <Button onClick={() => saveEditing(row.artwork_id)} size="sm" className="bg-[var(--color-primary)] text-white rounded-none h-8 px-2"><Check className="h-3 w-3" /></Button>
                            <Button onClick={() => setEditingRow(null)} size="sm" variant="ghost" className="rounded-none h-8 px-2"><X className="h-3 w-3" /></Button>
                          </div>
                        ) : <Button onClick={() => startEditing(row)} size="sm" variant="ghost" className="rounded-none h-8 px-2"><Edit2 className="h-3 w-3" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="p-4 bg-[#F9FAFB] border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {editingShippingTax === eventGroup.event_id ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Shipping:</span>
                          <Input type="number" step="0.01" value={shippingTaxValues.shipping_cost} onChange={(e) => setShippingTaxValues({ ...shippingTaxValues, shipping_cost: parseFloat(e.target.value) || 0 })} className="w-24 h-8 rounded-none border-2" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Tax:</span>
                          <Input type="number" step="0.01" value={shippingTaxValues.tax_cost} onChange={(e) => setShippingTaxValues({ ...shippingTaxValues, tax_cost: parseFloat(e.target.value) || 0 })} className="w-24 h-8 rounded-none border-2" />
                        </div>
                        <Button onClick={() => saveShippingTax(eventGroup.event_id)} size="sm" className="bg-[var(--color-primary)] text-white rounded-none h-8"><Check className="h-3 w-3 mr-1" />Save</Button>
                        <Button onClick={() => setEditingShippingTax(null)} size="sm" variant="ghost" className="rounded-none h-8"><X className="h-3 w-3 mr-1" />Cancel</Button>
                      </>
                    ) : (
                      <>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Shipping:</span> <span className="font-semibold">${eventGroup.shipping_cost.toFixed(2)}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Tax:</span> <span className="font-semibold">${eventGroup.tax_cost.toFixed(2)}</span>
                        </div>
                        <Button onClick={() => startEditingShippingTax(eventGroup.event_id, eventGroup.shipping_cost, eventGroup.tax_cost, eventGroup.is_frozen)} size="sm" variant="ghost" className="rounded-none h-8" data-testid={`edit-shipping-tax-${eventGroup.event_id}`}>
                          <Edit2 className="h-3 w-3 mr-1" />Edit Shipping & Tax
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Subtotal: ${eventGroup.subtotal.toFixed(2)}</p>
                    <p className="text-lg font-bold">Grand Total: ${eventGroup.grand_total.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pricing Tiers Component
function PricingTiers() {
  const [rates, setRates] = useState({});
  const [quantityRanges, setQuantityRanges] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isAddRangeOpen, setIsAddRangeOpen] = useState(false);
  const [newRange, setNewRange] = useState({ label: "", min: "", max: "" });

  const artworkTypes = ["Agenda", "Pocket Folder", "Name Badge", "Roller Banner", "Feedback Form", "Registration List"];

  const fetchRanges = useCallback(async () => { try { const r = await axios.get(`${API}/admin/quantity-ranges`); setQuantityRanges(r.data); } catch (e) { console.error("Failed to load quantity ranges", e); } }, []);
  const fetchRates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/pricing-tiers`);
      const rateMap = {};
      r.data.forEach(t => { rateMap[`${t.artwork_type}-${t.min_quantity}-${t.max_quantity || 'null'}`] = t.rate; });
      setRates(rateMap);
    } catch (e) { toast.error("Failed to load rates"); }
  }, []);

  useEffect(() => { fetchRanges(); fetchRates(); }, [fetchRanges, fetchRates]);

  const addRange = async () => {
    const minVal = parseInt(newRange.min); const maxVal = newRange.max ? parseInt(newRange.max) : null;
    const label = newRange.label || (maxVal ? `${minVal} - ${maxVal}` : `${minVal}+`);
    if (!minVal || minVal < 0) { toast.error("Min quantity required"); return; }
    try {
      await axios.post(`${API}/admin/quantity-ranges`, { label, min_quantity: minVal, max_quantity: maxVal });
      toast.success("Range added"); setNewRange({ label: "", min: "", max: "" }); setIsAddRangeOpen(false);
      await fetchRanges(); await fetchRates();
    } catch (e) { toast.error("Failed to add range"); }
  };

  const deleteRange = async (rangeId) => {
    if (!window.confirm("Delete this range and all its rates?")) return;
    try { await axios.delete(`${API}/admin/quantity-ranges/${rangeId}`); toast.success("Range deleted"); await fetchRanges(); await fetchRates(); } catch (e) { toast.error("Failed"); }
  };

  const saveRate = async (artworkType, range) => {
    const rate = parseFloat(editValue) || 0;
    try {
      const r = await axios.get(`${API}/admin/pricing-tiers`);
      const existing = r.data.find(t => t.artwork_type === artworkType && t.min_quantity === range.min_quantity && (t.max_quantity === range.max_quantity || (t.max_quantity === null && range.max_quantity === null)));
      if (existing) await axios.put(`${API}/admin/pricing-tiers/${existing.id}`, { artwork_type: artworkType, min_quantity: range.min_quantity, max_quantity: range.max_quantity, rate });
      else await axios.post(`${API}/admin/pricing-tiers`, { artwork_type: artworkType, min_quantity: range.min_quantity, max_quantity: range.max_quantity, rate });
      toast.success("Rate updated"); fetchRates(); setEditingCell(null);
    } catch (e) { toast.error("Failed"); }
  };

  const getRateValue = (artworkType, range) => rates[`${artworkType}-${range.min_quantity}-${range.max_quantity || 'null'}`] || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-3xl font-semibold tracking-tight">Rate Adjustment</h2><p className="text-sm text-muted-foreground mt-1">Quantity-based pricing tiers</p></div>
        <Button onClick={() => setIsAddRangeOpen(true)} className="bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none" data-testid="add-range-btn"><Plus className="h-4 w-4 mr-2" /> Add Range</Button>
      </div>
      {quantityRanges.length === 0 ? (
        <div className="border border-border bg-white p-8 text-center"><p className="text-muted-foreground">No quantity ranges defined.</p></div>
      ) : (
        <div className="border border-border bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold bg-[#F4F4F5]">Artwork Type</TableHead>
                {quantityRanges.map((range) => (
                  <TableHead key={range.id} className="font-semibold bg-[#F4F4F5] text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span>{range.label}</span>
                      <Button variant="ghost" size="sm" onClick={() => deleteRange(range.id)} className="h-5 w-5 p-0 rounded-none hover:bg-destructive hover:text-white"><X className="h-2.5 w-2.5" /></Button>
                    </div>
                    <span className="text-xs font-normal text-muted-foreground">Rate ($)</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {artworkTypes.map((artworkType) => (
                <TableRow key={artworkType}>
                  <TableCell className="font-medium">{artworkType}</TableCell>
                  {quantityRanges.map((range) => {
                    const cellKey = `${artworkType}-${range.min_quantity}`;
                    const isEditing = editingCell === cellKey;
                    const rateValue = getRateValue(artworkType, range);
                    return (
                      <TableCell key={range.id} className="text-center">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <Input type="number" step="0.01" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-20 h-7 rounded-none border-2 text-center" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveRate(artworkType, range); if (e.key === 'Escape') setEditingCell(null); }} />
                            <Button onClick={() => saveRate(artworkType, range)} size="sm" className="h-7 px-1.5 bg-[var(--color-primary)] text-white rounded-none"><Check className="h-3 w-3" /></Button>
                            <Button onClick={() => setEditingCell(null)} size="sm" variant="ghost" className="h-7 px-1.5 rounded-none"><X className="h-3 w-3" /></Button>
                          </div>
                        ) : (
                          <div onClick={() => { setEditingCell(cellKey); setEditValue(rateValue || "0"); }} className="cursor-pointer hover:bg-[#F4F4F5] p-1.5 transition-colors">
                            <span className="font-semibold">${rateValue.toFixed(2)}</span>
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="mt-4 p-3 border border-border bg-[#F4F4F5] text-sm text-muted-foreground">
        Rates auto-apply to invoices based on <strong>Print Quantity</strong>. <span className="text-green-600">Green</span> = matches rate card, <span className="text-red-600">Red</span> = modified.
      </div>
      <Dialog open={isAddRangeOpen} onOpenChange={setIsAddRangeOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="text-2xl font-semibold">Add Quantity Range</DialogTitle><DialogDescription>Define a new print quantity range</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium uppercase block mb-1">Min Quantity</label><Input type="number" value={newRange.min} onChange={(e) => setNewRange({ ...newRange, min: e.target.value })} className="rounded-none border-2" data-testid="range-min-input" /></div>
              <div><label className="text-xs font-medium uppercase block mb-1">Max Quantity</label><Input type="number" value={newRange.max} onChange={(e) => setNewRange({ ...newRange, max: e.target.value })} className="rounded-none border-2" data-testid="range-max-input" /></div>
            </div>
            <div><label className="text-xs font-medium uppercase block mb-1">Label</label><Input value={newRange.label} onChange={(e) => setNewRange({ ...newRange, label: e.target.value })} className="rounded-none border-2" data-testid="range-label-input" /></div>
            <Button onClick={addRange} className="w-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none px-6 py-3" data-testid="submit-range-btn">Add Range</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main App Component
function MainApp() {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [eventProgress, setEventProgress] = useState({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [staleDuplicates, setStaleDuplicates] = useState({ count: 0, details: [] });

  const checkStaleDuplicates = useCallback(async () => {
    if (user?.role !== "admin") return;
    try {
      const r = await axios.post(`${API}/api/proof/proof-approvals/cleanup-superseded?dry_run=true`);
      setStaleDuplicates({ count: r.data.found || 0, details: r.data.details || [] });
    } catch (e) {
      // Silent fail — banner just won't show
    }
  }, [user?.role]);

  const fixStaleDuplicates = async () => {
    const { count, details } = staleDuplicates;
    if (count === 0) return;
    const lines = details.slice(0, 10).map(d => `• ${d.artwork_name} (${d.customer_email})`).join("\n");
    const more = details.length > 10 ? `\n…and ${details.length - 10} more` : "";
    if (!window.confirm(`Found ${count} pending approval${count === 1 ? "" : "s"} superseded by an approved/rejected duplicate:\n\n${lines}${more}\n\nMark them as 'superseded' (stops reminders)?`)) return;
    try {
      const res = await axios.post(`${API}/api/proof/proof-approvals/cleanup-superseded?dry_run=false`);
      toast.success(`Cleaned up ${res.data.applied} stale duplicate${res.data.applied === 1 ? "" : "s"}`);
      setStaleDuplicates({ count: 0, details: [] });
    } catch (e) {
      toast.error("Cleanup failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const fetchProgress = useCallback(async () => { try { const r = await axios.get(`${API}/events/progress`); const map = {}; r.data.forEach(p => { map[p.event_id] = p; }); setEventProgress(map); } catch (e) { console.error("Failed to load progress", e); } }, []);
  const fetchEvents = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events`);
      setEvents(r.data);
      setSelectedEvent(prev => prev || (r.data.length > 0 ? r.data[0] : null));
    } catch (e) { toast.error("Failed to load events"); }
  }, []);
  const fetchArtworks = useCallback(async (eventId) => { try { const r = await axios.get(`${API}/artworks?event_id=${eventId}`); setArtworks(r.data); fetchProgress(); } catch (e) { toast.error("Failed to load artworks"); } }, [fetchProgress]);

  useEffect(() => { fetchEvents(); fetchProgress(); checkStaleDuplicates(); }, [fetchEvents, fetchProgress, checkStaleDuplicates]);
  useEffect(() => { if (selectedEvent) fetchArtworks(selectedEvent.id); }, [selectedEvent, fetchArtworks]);
  const createEvent = async () => {
    if (!newEventName.trim()) { toast.error("Event name is required"); return; }
    setIsLoading(true);
    try { const r = await axios.post(`${API}/events`, { name: newEventName }); toast.success("Event created"); setNewEventName(""); setIsCreateDialogOpen(false); await fetchEvents(); setSelectedEvent(r.data); } catch (e) { toast.error("Failed to create event"); } finally { setIsLoading(false); }
  };
  const deleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try { await axios.delete(`${API}/events/${eventId}`); toast.success("Event deleted"); await fetchEvents(); if (selectedEvent?.id === eventId) setSelectedEvent(events.length > 1 ? events.find(e => e.id !== eventId) : null); } catch (e) { toast.error("Failed"); }
  };
  const updateArtworkStatus = async (artworkId, newStatus) => { try { await axios.put(`${API}/artworks/${artworkId}/status`, { status: newStatus }); fetchArtworks(selectedEvent.id); toast.success("Status updated"); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="min-h-screen bg-white p-8 md:p-12">
      <div className="max-w-[1800px] mx-auto">
        <div className="mb-12 flex items-start justify-between">
          <div>
            <div className="mb-3"><Logo size={28} wordClassName="text-base" /></div>
            <h1 className="text-5xl font-bold tracking-tight mb-2" data-testid="page-title">CMYK Control Center</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium">{user.full_name}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
              {user.role === "admin" && <span className="inline-flex items-center gap-1 text-xs mt-1 px-2 py-0.5 bg-[var(--color-primary)] text-white"><Shield className="h-3 w-3" />Admin</span>}
            </div>
            <Button onClick={logout} variant="ghost" className="rounded-none"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>

        <Tabs defaultValue="kanban" className="w-full">
          {staleDuplicates.count > 0 && user.role === "admin" && (
            <div
              data-testid="stale-duplicates-banner"
              className="mb-6 border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-start justify-between gap-4"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-900 text-sm">
                    {staleDuplicates.count} customer{staleDuplicates.count === 1 ? "" : "s"} may be receiving incorrect reminders
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Pending proof approvals exist for artworks that already have an approved/rejected duplicate. Click "Fix Now" to stop the reminders.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  data-testid="stale-duplicates-fix-btn"
                  onClick={fixStaleDuplicates}
                  className="bg-amber-600 text-white hover:bg-amber-700 rounded-none h-9 px-4 text-sm"
                >
                  Fix Now
                </Button>
                <Button
                  data-testid="stale-duplicates-dismiss-btn"
                  onClick={() => setStaleDuplicates({ count: 0, details: [] })}
                  variant="ghost"
                  className="rounded-none h-9 px-2 text-amber-700 hover:bg-amber-100"
                  title="Dismiss until next page reload"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-6">
            <TabsList className="rounded-none border border-border">
              <TabsTrigger value="kanban" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><Package className="h-4 w-4 mr-2" />Kanban Board</TabsTrigger>
              <TabsTrigger value="shipping" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><Truck className="h-4 w-4 mr-2" />Shipping Details</TabsTrigger>
              {user.role === "admin" && (
                <>
                  <TabsTrigger value="proofs" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><Mail className="h-4 w-4 mr-2" />Proofs</TabsTrigger>
                  <TabsTrigger value="invoices" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><FileText className="h-4 w-4 mr-2" />Invoices</TabsTrigger>
                  <TabsTrigger value="pricing" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><DollarSign className="h-4 w-4 mr-2" />Rate Adjustment</TabsTrigger>
                  <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><Users className="h-4 w-4 mr-2" />Users</TabsTrigger>
                  <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-[var(--color-primary)] data-[state=active]:text-white"><Clock className="h-4 w-4 mr-2" />Audit Logs</TabsTrigger>
                </>
              )}
            </TabsList>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-event-button" className="bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none font-medium px-6 py-3"><Plus className="h-4 w-4 mr-2" />New Event</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="text-2xl font-semibold">Create New Event</DialogTitle><DialogDescription>Enter a name for your print shop event</DialogDescription></DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input data-testid="event-name-input" placeholder="Event name" value={newEventName} onChange={(e) => setNewEventName(e.target.value)} className="rounded-none border-2" onKeyPress={(e) => e.key === 'Enter' && createEvent()} />
                  <Button data-testid="submit-event-button" onClick={createEvent} disabled={isLoading} className="w-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none px-6 py-3">{isLoading ? "Creating..." : "Create Event"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <TabsContent value="kanban" className="mt-0">
            <KanbanBoard events={events} selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} artworks={artworks} fetchArtworks={fetchArtworks} fetchEvents={fetchEvents} deleteEvent={deleteEvent} updateArtworkStatus={updateArtworkStatus} eventProgress={eventProgress} />
          </TabsContent>
          <TabsContent value="shipping" className="mt-0"><ShipmentsTable /></TabsContent>
          {user.role === "admin" && (
            <>
              <TabsContent value="proofs" className="mt-0"><ProofApprovalManagement events={events} /></TabsContent>
              <TabsContent value="invoices" className="mt-0"><InvoiceManagement /></TabsContent>
              <TabsContent value="pricing" className="mt-0"><PricingTiers /></TabsContent>
              <TabsContent value="users" className="mt-0"><UserManagement /></TabsContent>
              <TabsContent value="audit" className="mt-0"><AuditLogs /></TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  return user ? <MainApp /> : <LandingPage />;
}

function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWrapper;
